// Two-step delete for user-added layers. A layer the user created on the canvas is
// bespoke work an AI revise will NOT recreate, so a stray click shouldn't nuke it —
// the first click arms ("Sure?"), a second within a few seconds confirms. AI-seeded
// layers (which always carry a `role`) delete immediately, as before.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Layer } from '../types'
import type { CompositionEditor } from './use-composition-editor'

/** AI-seeded layers always carry a `role`; layers added on the canvas don't. */
export function isUserAdded(layer: Layer): boolean {
  return !layer.role
}

/**
 * Deleting requires the two-step confirm for layers a stray tap shouldn't nuke:
 *  - user-added layers (bespoke work an AI revise won't recreate), and
 *  - the structural Background photo (deleting it blanks the post; the user almost
 *    always means "Replace photo", which is one tap away).
 * Other seeded decoration (grades, scrim, logo) still deletes immediately.
 */
export function requiresDeleteConfirm(layer: Layer): boolean {
  return isUserAdded(layer) || layer.role === 'background'
}

const CONFIRM_MS = 3000

export function useConfirmableDelete(editor: CompositionEditor) {
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear the arm-timeout on unmount so it can't setState after the toolbar is gone.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )

  const requestDelete = useCallback(
    (layer: Layer) => {
      if (!requiresDeleteConfirm(layer)) {
        editor.deleteLayer(layer.id)
        return
      }
      if (confirmingId === layer.id) {
        if (timer.current) clearTimeout(timer.current)
        timer.current = null
        setConfirmingId(null)
        editor.deleteLayer(layer.id)
        return
      }
      if (timer.current) clearTimeout(timer.current)
      setConfirmingId(layer.id)
      timer.current = setTimeout(() => setConfirmingId(null), CONFIRM_MS)
    },
    [confirmingId, editor]
  )

  return { confirmingId, requestDelete }
}
